import type { UserUuid } from '../../types/identity';
// src/api/messaging/index.ts

// Re-export all types
export * from '../../types/messaging';

// Re-export all messaging APIs
export * from './ConversationsApi';
export * from './MessagesApi';
export * from './TypingApi';

// Convenience exports for common operations
export {
	getConversations,
	getConversation,
	getQueueConversations,
	createPatientQueueConversation,
	createDirectConversation,
	createGroupConversation,
	findOrCreateDirectConversation,
	addParticipant,
	removeParticipant,
	getParticipants,
	assignConversation,
	claimConversation,
	archiveConversation,
	unarchiveConversation,
	closeConversation,
} from './ConversationsApi';

export {
	getMessages,
	sendMessage,
	editMessage,
	deleteMessage,
	markMessageAsRead,
	markConversationAsRead,
	getReadReceipts,
	loadMoreMessages,
	formatMessageContent,
	groupMessagesByDate,
	isOwnMessage,
	getTimeAgo,
} from './MessagesApi';

export {
	startTyping,
	stopTyping,
	getActiveTypers,
	TypingIndicatorManager,
} from './TypingApi';

// Export API base URL for consistency
export { API_BASE_URL } from './ConversationsApi';

// Helper functions
export const getUnreadConversationsCount = (conversations: any[]): number => {
	return conversations.filter((conv) => (conv.unread_count || 0) > 0).length;
};

export const getTotalUnreadMessagesCount = (conversations: any[]): number => {
	return conversations.reduce((total, conv) => total + (conv.unread_count || 0), 0);
};

export const sortConversationsByLastMessage = (conversations: any[]): any[] => {
	return [...conversations].sort((a, b) => {
		const dateA = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
		const dateB = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
		return dateB - dateA;
	});
};

export const getConversationTitle = (conversation: any, currentUserId: UserUuid | null): string => {
	// For patient_queue conversations the DB title is "Message from <patient name>" —
	// useful for the queue list but wrong for the patient themselves. Handle it separately.
	if (conversation.type !== 'patient_queue' && conversation.title) {
		return conversation.title;
	}

	if ((conversation.type === 'direct' || conversation.type === 'patient_queue') && conversation.participants) {
		if (currentUserId) {
			const other = conversation.participants.find(
				(p: any) => p.user_id !== currentUserId && p.user
			);
			if (other) {
				return `${other.user.first_name} ${other.user.last_name}`;
			}
		}

		// Fallback when userId hasn't loaded yet: for a patient_queue conversation
		// always prefer the practitioner as the title so the patient sees the doctor's name.
		if (conversation.type === 'patient_queue') {
			const practitioner = conversation.participants.find(
				(p: any) => p.role === 'practitioner' && p.user
			);
			if (practitioner) {
				return `${practitioner.user.first_name} ${practitioner.user.last_name}`;
			}
			// No practitioner assigned yet — fall back to the stored title for practitioners,
			// or a generic label for the patient
			return conversation.title || 'Care Team';
		}
	}

	return conversation.title || 'Conversation';
};

export const getConversationParticipantNames = (conversation: any, currentUserId: UserUuid | null): string => {
	if (!conversation.participants) return '';

	const others = conversation.participants
		.filter((p: any) => (!currentUserId || p.user_id !== currentUserId) && p.user)
		.map((p: any) => `${p.user.first_name} ${p.user.last_name}`);

	if (others.length === 0) return 'You';
	if (others.length === 1) return others[0];
	if (others.length === 2) return others.join(' and ');
	return `${others[0]} and ${others.length - 1} others`;
};